import { Pagination } from "antd";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Card, Input, Table, Badge } from "reactstrap";
import { FiExternalLink, FiX, FiArrowRight, FiBookmark, FiCornerDownRight } from "react-icons/fi";
import { getDocByKeyValue } from "../../firebase/utility";
import { formatNumberWithCommas } from "../../helpers/helpers";
import { getOrderIDColor } from "../../utility";
import { normalizeStatusValue } from "../../utils/normalizeStatusValue";
import useRowSelection from "../../views/Admin/customHooks/useRowSelection";
import NotFoundDataUI from "../cards/NotFoundDataUI";

const getPaymentColor = (status) => {
  switch (status) {
    case "Paid": return "#27AE60";
    case "Partially Paid": return "#F2994A";
    case "Refunded": return "#9747FF";
    case "Failed": return "#FF4D4F";
    default: return "#F2994A";
  }
};

const StatusPill = ({ label, color }) => (
  <span style={{
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "20px",
    backgroundColor: `${color}20`,
    color: color,
    border: `1px solid ${color}50`,
    fontSize: "12px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
    letterSpacing: "0.3px"
  }}>
    {label}
  </span>
);

const SalesOrderTable = ({
  sale_orders,
  setSelectedData,
  user,
  setSelectedOrderStatus,
  searchValue,
  showCriticalOrders,
  totalPages,
  page,
  pageSize,
  setPageSize,
  setPage,
  selectedOrderStatus,
  onClearSearch,
}) => {
  const history = useHistory();
  const {
    selectedRows,
    handleCheckboxChange,
    handleSelectAll,
    handleViewSelected,
  } = useRowSelection();

  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // "Moved order" banner state
  const [movedOrderInfo, setMovedOrderInfo] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [scrollToOrder, setScrollToOrder] = useState(null);
  const tableRef = useRef(null);

  const prevSearchValue = useRef(searchValue);
  const prevShowCritical = useRef(showCriticalOrders);

  // Initialize selected order highlight
  useEffect(() => {
    const storedSelectedOrder = localStorage.getItem("SelectedSaleOrder");
    if (storedSelectedOrder) {
      setSelectedOrder(storedSelectedOrder);
    }
  }, []);

  // Reset page to 1 ONLY when search value or critical orders toggle ACTUALLY changes
  useEffect(() => {
    let shouldResetPage = false;
    if (prevSearchValue.current !== searchValue) {
      shouldResetPage = true;
      prevSearchValue.current = searchValue;
    }
    if (prevShowCritical.current !== showCriticalOrders) {
      shouldResetPage = true;
      prevShowCritical.current = showCriticalOrders;
    }
    if (shouldResetPage) {
      setPage(1);
      localStorage.setItem("saleOrderPage", 1);
    }
  }, [searchValue, showCriticalOrders, setPage]);

  // Handle out-of-bounds pagination
  useEffect(() => {
    const storedPage = parseInt(localStorage.getItem("saleOrderPage"), 10) || 1;
    if (totalPages > 0 && storedPage > totalPages) {
      setPage(1);
      localStorage.setItem("saleOrderPage", 1);
    }
  }, [totalPages, setPage]);

  // Detect if the previously viewed order has moved out of the current filtered list
  useEffect(() => {
    const storedOrderId = localStorage.getItem("SelectedSaleOrder");
    const storedPrevStatus = localStorage.getItem("SelectedSaleOrderPrevStatus");
    const storedOrderDocId = localStorage.getItem("SelectedSaleOrderDocId");

    if (!storedOrderId || !storedPrevStatus || bannerDismissed) return;

    // Check if the order is in the current list
    const isInList = sale_orders?.some((o) => o.id === storedOrderId);

    if (!isInList && sale_orders?.length >= 0 && selectedOrderStatus === storedPrevStatus) {
      // Order was in this status tab but is now gone — fetch its current status
      if (storedOrderDocId) {
        firebase
          .firestore()
          .collection("Orders")
          .doc(storedOrderDocId)
          .get()
          .then((doc) => {
            if (doc.exists) {
              const currentData = doc.data();
              if (currentData?.status !== storedPrevStatus) {
                setMovedOrderInfo({
                  orderId: storedOrderId,
                  docId: storedOrderDocId,
                  previousStatus: storedPrevStatus,
                  currentStatus: currentData?.status ?? "Unknown",
                });
              }
            }
          })
          .catch(() => {});
      }
    } else if (isInList) {
      // Order is still in the list, clear any stale moved info
      setMovedOrderInfo(null);
    }
  }, [sale_orders, selectedOrderStatus, bannerDismissed]);

  // Compute the ghost row insertion index based on stored neighbor IDs
  const ghostRowIndex = (() => {
    if (!movedOrderInfo || bannerDismissed || !sale_orders?.length) return -1;
    const neighborAboveId = localStorage.getItem("SelectedSaleOrderNeighborAbove");
    const neighborBelowId = localStorage.getItem("SelectedSaleOrderNeighborBelow");
    if (!neighborAboveId && !neighborBelowId) return 0;

    // Try to find the neighbor above — ghost row goes right after it
    if (neighborAboveId) {
      const aboveIdx = sale_orders.findIndex((o) => o.id === neighborAboveId);
      if (aboveIdx !== -1) return aboveIdx + 1;
    }
    // Fallback: find the neighbor below — ghost row goes right before it
    if (neighborBelowId) {
      const belowIdx = sale_orders.findIndex((o) => o.id === neighborBelowId);
      if (belowIdx !== -1) return belowIdx;
    }
    // If neither neighbor is found (e.g., different page), show at top
    return 0;
  })();

  const toggleDropdown = (rowId) => {
    setOpenDropdownRow(openDropdownRow === rowId ? null : rowId);
  };

  useEffect(() => {
    let selected = handleViewSelected(sale_orders);
    setSelectedData(selected);
  }, [selectedRows]);

  const clickOnPo = async (poId) => {
    let data = await getDocByKeyValue("PurchaseOrders", "id", poId);
    let poData = data[0];
    history.push({
      pathname: `/admin/update-purchase-orders-details/${poData?._id}`,
      state: {
        obj: poData,
        vendor: {
          billing: [poData?.vendor],
          id: poData?.vendorOrderId,
          _id: poData?.vendorId,
        },
      },
    });
  };

  const dismissBanner = () => {
    setBannerDismissed(true);
    setMovedOrderInfo(null);
    localStorage.removeItem("SelectedSaleOrder");
    localStorage.removeItem("SelectedSaleOrderPrevStatus");
    localStorage.removeItem("SelectedSaleOrderDocId");
    localStorage.removeItem("SelectedSaleOrderNeighborAbove");
    localStorage.removeItem("SelectedSaleOrderNeighborBelow");
  };

  const goToMovedOrder = () => {
    if (movedOrderInfo?.docId) {
      history.push({
        pathname: `/admin/sale-order-details/${movedOrderInfo.docId}`,
      });
    }
  };

  const switchToNewStatus = () => {
    if (movedOrderInfo?.currentStatus) {
      const orderId = movedOrderInfo.orderId;
      // Keep the selected order so it gets highlighted in the new tab
      localStorage.setItem("selectedOrderStatus", movedOrderInfo.currentStatus);
      localStorage.setItem("SelectedSaleOrder", orderId);
      setSelectedOrder(orderId);
      setSelectedOrderStatus(movedOrderInfo.currentStatus);
      setScrollToOrder(orderId);
      // Clear banner/ghost state without clearing SelectedSaleOrder
      setBannerDismissed(true);
      setMovedOrderInfo(null);
      localStorage.removeItem("SelectedSaleOrderPrevStatus");
      localStorage.removeItem("SelectedSaleOrderDocId");
      localStorage.removeItem("SelectedSaleOrderNeighborAbove");
      localStorage.removeItem("SelectedSaleOrderNeighborBelow");
    }
  };

  // Scroll to the order row after the list re-renders with new data
  useEffect(() => {
    if (!scrollToOrder || !sale_orders?.length) return;
    // Check if the order exists in the current list
    const orderInList = sale_orders.some((o) => o.id === scrollToOrder);
    if (orderInList) {
      // Small delay to let the DOM render the new rows
      const timer = setTimeout(() => {
        const row = document.querySelector(`[data-order-id="${scrollToOrder}"]`);
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
          // Add a brief pulse animation
          row.classList.add("scroll-highlight-pulse");
          setTimeout(() => row.classList.remove("scroll-highlight-pulse"), 2000);
        }
        setScrollToOrder(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scrollToOrder, sale_orders]);

  return (
    <div className="modern-table-container">
      {/* Moved Order Banner */}
      {movedOrderInfo && !bannerDismissed && (
        <div className="moved-order-banner">
          <div className="moved-order-banner__content">
            <div className="moved-order-banner__icon">
              <FiBookmark size={18} />
            </div>
            <div className="moved-order-banner__text">
              <span className="moved-order-banner__title">Order Moved</span>
              <span className="moved-order-banner__detail">
                <strong>#{movedOrderInfo.orderId}</strong> was moved from{" "}
                <span className="moved-order-banner__status moved-order-banner__status--from">
                  {movedOrderInfo.previousStatus}
                </span>
                <FiArrowRight size={14} className="mx-1" />
                <span className="moved-order-banner__status moved-order-banner__status--to">
                  {movedOrderInfo.currentStatus}
                </span>
              </span>
            </div>
            <div className="moved-order-banner__actions">
              <button
                className="moved-order-banner__btn moved-order-banner__btn--view"
                onClick={goToMovedOrder}
                title="View this order"
              >
                <FiExternalLink size={14} />
                View Order
              </button>
              <button
                className="moved-order-banner__btn moved-order-banner__btn--switch"
                onClick={switchToNewStatus}
                title={`Go to ${movedOrderInfo.currentStatus} tab`}
              >
                Go to {movedOrderInfo.currentStatus}
              </button>
              <button
                className="moved-order-banner__btn moved-order-banner__btn--dismiss"
                onClick={dismissBanner}
                title="Dismiss"
              >
                <FiX size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
      <Table
        responsive
        hover
        bordered
        className="mb-0"
        style={{
          fontSize: "14px",
          overflow: "hidden",
          border: "1px solid #e2e8f0"
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#00509d", color: "white" }}>
            {(user?.role === "Admin" || user?.role === "Super-admin") && (
              <th className="text-center" style={{ width: '50px', borderColor: 'rgba(255,255,255,0.3)' }}>
                <Input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e, sale_orders)}
                  checked={selectedRows.length === sale_orders.length && sale_orders.length > 0}
                  className="cursor-pointer"
                />
              </th>
            )}
            <th className="text-center py-2" >CREATED AT</th>
            <th className="text-center py-2" >ORDER ID</th>
            <th className="text-center py-2" >QB ID</th>
            <th className="text-center py-2" >STATUS</th>
            <th className="text-center py-2" >PAYMENT</th>
            <th className="text-center py-2" >CUSTOMER</th>
            <th className="text-center py-2" >PO</th>
            <th className="text-center py-2" >AMOUNT</th>
          </tr>
        </thead>
        <tbody style={{ backgroundColor: '#fff' }}>
          {sale_orders && sale_orders.length > 0 ? (
            (() => {
              const colSpan = (user?.role === "Admin" || user?.role === "Super-admin") ? 9 : 8;
              const rows = [];

              // Ghost row renderer
              const renderGhostRow = (key) => (
                <tr key={key} className="ghost-order-row">
                  <td colSpan={colSpan} style={{ padding: 0, border: 'none' }}>
                    <div className="ghost-order-row__inner">
                      <div className="ghost-order-row__line ghost-order-row__line--left" />
                      <div className="ghost-order-row__badge">
                        <FiCornerDownRight size={13} className="ghost-order-row__arrow" />
                        <span className="ghost-order-row__label">
                          <strong>#{movedOrderInfo.orderId}</strong> was here
                        </span>
                        <span className="ghost-order-row__separator">&bull;</span>
                        <span className="ghost-order-row__moved">
                          moved to{" "}
                          <span className="ghost-order-row__new-status">
                            {movedOrderInfo.currentStatus}
                          </span>
                        </span>
                        <button
                          className="ghost-order-row__view-btn"
                          onClick={goToMovedOrder}
                        >
                          <FiExternalLink size={12} />
                          View
                        </button>
                        <button
                          className="ghost-order-row__go-btn"
                          onClick={switchToNewStatus}
                        >
                          Go to {movedOrderInfo.currentStatus}
                        </button>
                        <button
                          className="ghost-order-row__dismiss-btn"
                          onClick={dismissBanner}
                          title="Dismiss"
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                      <div className="ghost-order-row__line ghost-order-row__line--right" />
                    </div>
                  </td>
                </tr>
              );

              const showGhost = movedOrderInfo && !bannerDismissed && ghostRowIndex >= 0;

              sale_orders.forEach((row, index) => {
                // Insert ghost row before this index if applicable
                if (showGhost && index === ghostRowIndex) {
                  rows.push(renderGhostRow("ghost-row"));
                }

                const statusColor = getOrderIDColor(row.status);
                const paymentStatus =
                  normalizeStatusValue(row?.paymentStatus) || "Pending Payment";
                const paymentColor = getPaymentColor(paymentStatus);

                rows.push(
                  <tr
                    key={index}
                    data-order-id={row?.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                      borderBottom: "1px solid #f1f5f9"
                    }}
                    className={`sales-row hover-glow ${row.id === selectedOrder ? "active-highlighter" : ""}`}
                  >
                    {(user?.role === "Admin" || user?.role === "Super-admin") && (
                      <td className="text-center">
                        <Input
                          type="checkbox"
                          className="rounded-1"
                          onChange={(e) => handleCheckboxChange(e, row)}
                          checked={selectedRows.includes(row?._id)}
                        />
                      </td>
                    )}
                    <td className="text-center text-dark">
                      {moment(row.date_created).format("MMM DD, YYYY")}
                    </td>
                    <td className="text-center">
                      <div
                        onClick={() => {
                          localStorage.setItem("SelectedSaleOrder", row?.id);
                          localStorage.setItem("SelectedSaleOrderDocId", row?._id);
                          localStorage.setItem("SelectedSaleOrderPrevStatus", selectedOrderStatus);
                          const currentIdx = sale_orders.findIndex((o) => o.id === row?.id);
                          const neighborAbove = currentIdx > 0 ? sale_orders[currentIdx - 1]?.id : "";
                          const neighborBelow = currentIdx < sale_orders.length - 1 ? sale_orders[currentIdx + 1]?.id : "";
                          localStorage.setItem("SelectedSaleOrderNeighborAbove", neighborAbove);
                          localStorage.setItem("SelectedSaleOrderNeighborBelow", neighborBelow);
                          setBannerDismissed(false);
                          setMovedOrderInfo(null);
                          history.push({
                            pathname: `/admin/sale-order-details/${row?._id}`,
                            state: row,
                          });
                        }}
                        className="fw-bold pointer"
                        style={{ color: statusColor }}
                      >
                        #{row?.id}
                      </div>
                    </td>
                    <td className="text-center text-muted">
                      <small>{row?.quickbooks_id && row.quickbooks_id.trim() !== "" ? row.quickbooks_id : "—"}</small>
                    </td>
                    <td className="text-center">
                      <StatusPill label={row?.status ?? "New"} color={statusColor} />
                    </td>
                    <td className="text-center">
                      <StatusPill label={paymentStatus} color={paymentColor} />
                    </td>
                    <td className="text-center text-dark">
                      <span title={row?.billing?.company}>
                        {row?.billing?.company
                          ? (row?.billing?.company.length > 20
                            ? `${row?.billing?.company.slice(0, 18)}...`
                            : row?.billing?.company)
                          : "N/A"}
                      </span>
                    </td>
                    <td className="text-center">
                      {row?.poNumbers && row?.poNumbers.length > 0 ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <span
                            className="text-primary fw-medium pointer"
                            onClick={() => clickOnPo(row.poNumbers[0])}
                          >
                            {row.poNumbers[0]}
                          </span>
                          {row.poNumbers.length > 1 && (
                            <div className="position-relative ms-2">
                              <Badge
                                color="light"
                                className="text-primary border pointer"
                                onClick={() => toggleDropdown(row._id)}
                              >
                                +{row.poNumbers.length - 1}
                              </Badge>
                              {openDropdownRow === row._id && (
                                <Card
                                  className="shadow-lg border-0"
                                  style={{
                                    position: "absolute",
                                    zIndex: 999,
                                    padding: "8px",
                                    minWidth: "120px",
                                    top: "100%",
                                    left: "50%",
                                    transform: "translateX(-50%)"
                                  }}
                                >
                                  {row.poNumbers.slice(1).map((po, idx) => (
                                    <div
                                      key={idx}
                                      className="dropdown-item py-1 px-2 rounded pointer text-primary fs-13"
                                      onClick={() => clickOnPo(po)}
                                    >
                                      {po}
                                    </div>
                                  ))}
                                </Card>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small">N/A</span>
                      )}
                    </td>
                    <td className="text-center fw-bold text-dark">
                      ${formatNumberWithCommas(parseFloat(row?.total ?? 0).toFixed(2))}
                    </td>
                  </tr>
                );
              });

              // If ghost row goes at the very end (after all rows)
              if (showGhost && ghostRowIndex >= sale_orders.length) {
                rows.push(renderGhostRow("ghost-row-end"));
              }

              return rows;
            })()
          ) : (
            <tr>
              <td colSpan={(user?.role === "Admin" || user?.role === "Super-admin") ? 9 : 8} className="text-center py-5 border-0">
                <NotFoundDataUI 
                  message={"No sale orders found!"} 
                  onClearSearch={onClearSearch}
                />
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {sale_orders && sale_orders?.length > 0 && (
        <div className="d-flex justify-content-end p-3 bg-light border-top">
          <Pagination
            defaultCurrent={page}
            current={page}
            total={totalPages}
            onChange={(value) => {
              localStorage.setItem("saleOrderPage", value);
              setPage(value);
            }}
            pageSize={pageSize}
            showSizeChanger={true}
            onShowSizeChange={(current, size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={['10', '25', '50', '100']}
            className="premium-pagination"
          />
        </div>
      )}
    </div>

  )

};

export default SalesOrderTable;
